// src/components/about/ExperienceTimeline.tsx
import { useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';

import { aboutConfig, type AboutTimelineEngagement } from '../../config/aboutConfig';
import {
  TimelineRoot,
  TimelineContainer,
  SectionHeader,
  Eyebrow,
  EyebrowMark,
  Heading,
  Lede,
  PositionBar,
  PositionMain,
  Company,
  PositionRole,
  PositionMeta,
  PositionPeriod,
  PositionDuration,
  DesktopJourney,
  JourneyRail,
  JourneyTrunk,
  JourneyNode,
  NodeDot,
  NodeText,
  NodeClient,
  NodeDomain,
  NodeRef,
  EngagementPanel,
  EngagementBody,
  EngagementHead,
  EngagementClient,
  EngagementTitle,
  Block,
  BlockLabel,
  BlockText,
  BlockSplit,
  MarkerList,
  MarkerItem,
  TechList,
  Tech,
  DetailToggle,
  DetailRegion,
  DetailList,
  MobileJourney,
  MobileItem,
  MobileToggle,
  MobileDot,
  MobileText,
  MobileClient,
  MobileDomain,
  MobileChevron,
  MobileRegion,
  MobileBody,
  TimelineNote,
} from './ExperienceTimeline.styles';
import { motionSet, DURATION, EASE_OUT } from './motion';

const headingId = 'about-timeline-heading';
const detailLabel = 'What I worked on';

interface DetailProps {
  item: AboutTimelineEngagement;
  open: boolean;
  onToggle: () => void;
  reduceMotion: boolean;
}

const Detail = ({ item, open, onToggle, reduceMotion }: DetailProps) => (
  <>
    <DetailToggle type="button" onClick={onToggle} aria-expanded={open}>
      {detailLabel}
      <motion.span
        aria-hidden="true"
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: reduceMotion ? 0 : DURATION.micro, ease: EASE_OUT }}
      >
        <FaChevronDown />
      </motion.span>
    </DetailToggle>

    <AnimatePresence initial={false}>
      {open && (
        <DetailRegion
          key="detail"
          initial={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
          animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
          exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
          transition={{ duration: reduceMotion ? DURATION.panelInReduced : DURATION.morph, ease: EASE_OUT }}
        >
          <DetailList>
            {item.detail.map((entry) => (
              <MarkerItem key={entry}>{entry}</MarkerItem>
            ))}
          </DetailList>
        </DetailRegion>
      )}
    </AnimatePresence>
  </>
);

interface ContentProps {
  item: AboutTimelineEngagement;
  detailOpen: boolean;
  onToggleDetail: () => void;
  reduceMotion: boolean;
}

const EngagementContent = ({
  item,
  detailOpen,
  onToggleDetail,
  reduceMotion,
}: ContentProps) => (
  <>
    <Block>
      <BlockLabel>Business purpose</BlockLabel>
      <BlockText>{item.purpose}</BlockText>
    </Block>

    <Block>
      <BlockLabel>My contribution</BlockLabel>
      <BlockText>{item.contribution}</BlockText>
    </Block>

    <BlockSplit>
      <Block>
        <BlockLabel>Engineering responsibilities</BlockLabel>
        <MarkerList>
          {item.responsibilities.map((entry) => (
            <MarkerItem key={entry}>{entry}</MarkerItem>
          ))}
        </MarkerList>
      </Block>

      <Block>
        <BlockLabel>Technologies &amp; architecture</BlockLabel>
        <TechList>
          {item.technologies.map((tech) => (
            <Tech key={tech}>{tech}</Tech>
          ))}
        </TechList>
      </Block>
    </BlockSplit>

    <Detail
      item={item}
      open={detailOpen}
      onToggle={onToggleDetail}
      reduceMotion={reduceMotion}
    />
  </>
);

const ExperienceTimeline = () => {
  const { eyebrow, heading, lede, note, position, items } = aboutConfig.timeline;
  const reduceMotion = useReducedMotion() ?? false;

  const [activeId, setActiveId] = useState(items[0].id);
  const [openId, setOpenId] = useState<string | null>(items[0].id);
  const [detailOpenId, setDetailOpenId] = useState<string | null>(null);
  const nodeRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeIndex = items.findIndex((item) => item.id === activeId);
  const active = items[activeIndex] ?? items[0];
  const motion = motionSet(reduceMotion);

  const focusNode = (index: number) => {
    const bounded = (index + items.length) % items.length;
    setActiveId(items[bounded].id);
    nodeRefs.current[bounded]?.focus();
  };

  const handleNodeKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        focusNode(index + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        focusNode(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusNode(0);
        break;
      case 'End':
        event.preventDefault();
        focusNode(items.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <TimelineRoot aria-labelledby={headingId}>
      <TimelineContainer>
        <SectionHeader
          variants={motion.reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <Eyebrow>
            <EyebrowMark aria-hidden="true" />
            {eyebrow}
          </Eyebrow>
          <Heading id={headingId}>{heading}</Heading>
          <Lede>{lede}</Lede>
        </SectionHeader>

        <PositionBar
          variants={motion.reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
        >
          <PositionMain>
            <Company>{position.company}</Company>
            <PositionRole>{position.role}</PositionRole>
          </PositionMain>

          <PositionMeta>
            <PositionPeriod>{position.period}</PositionPeriod>
            <PositionDuration>{position.duration}</PositionDuration>
          </PositionMeta>
        </PositionBar>

        {/* ---------- Desktop: timeline rail + detail panel ---------- */}
        <DesktopJourney>
          <JourneyRail
            role="tablist"
            aria-orientation="vertical"
            aria-label="Client engagements"
          >
            <JourneyTrunk
              aria-hidden="true"
              initial={reduceMotion ? { opacity: 0 } : { scaleY: 0 }}
              whileInView={reduceMotion ? { opacity: 1 } : { scaleY: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: DURATION.reveal, ease: EASE_OUT }}
            />

            {items.map((item, index) => {
              const isActive = item.id === active.id;
              const isSeen = index <= activeIndex;

              return (
                <JourneyNode
                  key={item.id}
                  ref={(node) => {
                    nodeRefs.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`timeline-tab-${item.id}`}
                  aria-selected={isActive}
                  aria-controls={`timeline-panel-${item.id}`}
                  tabIndex={isActive ? 0 : -1}
                  $active={isActive}
                  $last={index === items.length - 1}
                  onClick={() => setActiveId(item.id)}
                  onKeyDown={(event) => handleNodeKeyDown(event, index)}
                >
                  <NodeDot $active={isActive} $seen={isSeen} aria-hidden="true" />

                  <NodeText>
                    <NodeClient $active={isActive}>{item.client}</NodeClient>
                    <NodeDomain $active={isActive}>{item.domain}</NodeDomain>
                  </NodeText>

                  <NodeRef aria-hidden="true">{item.ref}</NodeRef>
                </JourneyNode>
              );
            })}
          </JourneyRail>

          <EngagementPanel
            role="tabpanel"
            id={`timeline-panel-${active.id}`}
            aria-labelledby={`timeline-tab-${active.id}`}
            tabIndex={0}
          >
            <AnimatePresence mode="wait" initial={false}>
              <EngagementBody
                key={active.id}
                variants={motion.panel}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <EngagementHead>
                  <EngagementClient>{active.client}</EngagementClient>
                  <EngagementTitle>{active.domain}</EngagementTitle>
                </EngagementHead>

                <EngagementContent
                  item={active}
                  detailOpen={detailOpenId === active.id}
                  onToggleDetail={() =>
                    setDetailOpenId(detailOpenId === active.id ? null : active.id)
                  }
                  reduceMotion={reduceMotion}
                />
              </EngagementBody>
            </AnimatePresence>
          </EngagementPanel>
        </DesktopJourney>

        {/* ---------- Mobile: vertical timeline, one entry open ---------- */}
        <MobileJourney>
          {items.map((item, index) => {
            const isOpen = item.id === openId;

            return (
              <MobileItem key={item.id} $isLast={index === items.length - 1}>
                <MobileToggle
                  type="button"
                  $active={isOpen}
                  aria-expanded={isOpen}
                  aria-controls={`timeline-step-${item.id}`}
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                >
                  <MobileDot $active={isOpen} aria-hidden="true" />

                  <MobileText>
                    <MobileClient $active={isOpen}>{item.client}</MobileClient>
                    <MobileDomain $active={isOpen}>{item.domain}</MobileDomain>
                  </MobileText>

                  <MobileChevron
                    $active={isOpen}
                    aria-hidden="true"
                    animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: reduceMotion ? 0 : DURATION.micro, ease: EASE_OUT }}
                  >
                    <FaChevronDown />
                  </MobileChevron>
                </MobileToggle>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <MobileRegion
                      key="region"
                      id={`timeline-step-${item.id}`}
                      initial={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                      animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
                      exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                      transition={{ duration: reduceMotion ? DURATION.panelInReduced : DURATION.morph, ease: EASE_OUT }}
                    >
                      <MobileBody>
                        <EngagementContent
                          item={item}
                          detailOpen={detailOpenId === item.id}
                          onToggleDetail={() =>
                            setDetailOpenId(detailOpenId === item.id ? null : item.id)
                          }
                          reduceMotion={reduceMotion}
                        />
                      </MobileBody>
                    </MobileRegion>
                  )}
                </AnimatePresence>
              </MobileItem>
            );
          })}
        </MobileJourney>

        <TimelineNote>
          <strong>On scope and dates</strong> — {note}
        </TimelineNote>
      </TimelineContainer>
    </TimelineRoot>
  );
};

export default ExperienceTimeline;
